import React, { FormEventHandler, useCallback, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import log from 'loglevel'
import { RpcError } from 'grpc-web'
import { ComplianceFramework } from '@trustero/trustero-api-web/lib/audit/framework_pb'
import { useHideModal, useIsShowModal } from 'src/Modal/ModalStateContext'
import { getErrorMessage } from 'src/Utils/globalHelpers'
import { FrameworkSectionsEditor } from 'src/components/ModalForms/Frameworks/FrameworkSectionsEditor'
import {
  createEmptySectionDraft,
  getDuplicateNameErrors,
  getEmptyNameErrors,
  parseBackendDuplicateError,
} from 'src/components/ModalForms/Frameworks/FrameworkSectionsEditor.helpers'
import {
  FrameworkSectionsEditorErrors,
  SectionDraft,
} from 'src/components/ModalForms/Frameworks/FrameworkSectionsEditor.types'
import {
  useAddFrameworkSections,
  useExistingFrameworkNames,
  useFrameworkControls,
} from 'src/pages/Frameworks/frameworks.hooks'
import { ModalForm, ModalFormId } from '../..'

type Props = {
  framework: ComplianceFramework
}

export const AddFrameworkSectionsManualModal = ({
  framework,
}: Props): JSX.Element => {
  const cfModelId = framework.getModelId()
  const show = useIsShowModal(ModalFormId.ADD_FRAMEWORK_SECTIONS_MANUAL)
  const hide = useHideModal({
    modalId: ModalFormId.ADD_FRAMEWORK_SECTIONS_MANUAL,
  })
  const addFrameworkSections = useAddFrameworkSections()

  const {
    existingSectionNames,
    existingRequirementNames,
    sectionsData,
    isLoadingSections: sectionsLoading,
  } = useExistingFrameworkNames(framework, show)
  const { controlOptions, isLoading: controlsLoading } =
    useFrameworkControls(show)

  const [sections, setSections] = useState<SectionDraft[]>([
    createEmptySectionDraft(),
  ])
  const setSectionsEnsuringAtLeastOne: React.Dispatch<
    React.SetStateAction<SectionDraft[]>
  > = useCallback((updater) => {
    setSections((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater
      return next.length > 0 ? next : [createEmptySectionDraft()]
    })
  }, [])

  const [addErrors, setAddErrors] = useState<FrameworkSectionsEditorErrors>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  const closeAndReset = () => {
    setSections([createEmptySectionDraft()])
    setAddErrors({})
    setIsSubmitting(false)
    hide()
  }

  const onSubmit: FormEventHandler = async (e) => {
    e.preventDefault()
    setAddErrors({})

    const empty = getEmptyNameErrors(sections)
    const dupes = getDuplicateNameErrors(
      sections,
      existingSectionNames,
      existingRequirementNames,
    )
    const sectionNames = { ...empty.sectionNames, ...dupes.sectionNames }
    const requirementNames = {
      ...empty.requirementNames,
      ...dupes.requirementNames,
    }
    if (
      Object.keys(sectionNames).length ||
      Object.keys(requirementNames).length
    ) {
      setAddErrors({ sectionNames, requirementNames })
      return
    }

    setIsSubmitting(true)
    try {
      await addFrameworkSections({
        complianceFrameworkModelId: cfModelId,
        existingSections: sectionsData?.getSectionsList() ?? [],
        sections,
      })
      toast.success('Sections added.')
      closeAndReset()
    } catch (err) {
      const dupResult = parseBackendDuplicateError(err, sections)
      if (dupResult) {
        if (dupResult.editorErrors) setAddErrors(dupResult.editorErrors)
        if (dupResult.toast) toast.error(dupResult.toast)
        return
      }
      const errorMessage = getErrorMessage(err as RpcError)
      toast.error(errorMessage.message)
      if (errorMessage.shouldLog) {
        log.error('AddFrameworkSectionsManualModal submit failed', err)
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const errors: FrameworkSectionsEditorErrors = useMemo(
    () => ({
      sectionNames: addErrors.sectionNames,
      requirementNames: addErrors.requirementNames,
    }),
    [addErrors],
  )

  return (
    <ModalForm
      show={show}
      hide={closeAndReset}
      formId={ModalFormId.ADD_FRAMEWORK_SECTIONS_MANUAL}
      size="xl"
      title="Add Sections"
      submitText="Add sections"
      showOverflow
      disableSubmitButton={isSubmitting || controlsLoading || sectionsLoading}
    >
      <form id={ModalFormId.ADD_FRAMEWORK_SECTIONS_MANUAL} onSubmit={onSubmit}>
        <FrameworkSectionsEditor
          sections={sections}
          setSections={setSectionsEnsuringAtLeastOne}
          setErrors={setAddErrors}
          controlOptions={controlOptions}
          errors={errors}
        />
      </form>
    </ModalForm>
  )
}
